/* eslint-disable @atlassian/relay/unused-fields */
import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import type { SearchGraphResultProps } from '../../../../../../../common/types';

import type { searchGraphVideo_AtlassianSearchPage$key } from './__generated__/searchGraphVideo_AtlassianSearchPage.graphql';
import { MeetingTranscriptRenderer } from './meeting-transcript-renderer';
import type { LinkedResult } from './shared/types';
import { VideoResultRenderer } from './video-result-renderer';

export const SearchGraphVideo: ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	is3pGroupingEnabled,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	pastMeetingOccurrences,
	entityType,
}: SearchGraphResultProps<searchGraphVideo_AtlassianSearchPage$key>) => React.JSX.Element = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	is3pGroupingEnabled,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	pastMeetingOccurrences,
	entityType,
}: SearchGraphResultProps<searchGraphVideo_AtlassianSearchPage$key>) => {
	const data = useFragment(
		graphql`
			fragment searchGraphVideo_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				url
				excerpt
				lastModifiedDate
				permissionLevel
				integrationId
				entity {
					... on ExternalVideo {
						durationInSeconds
						thumbnailUrl
						chapters {
							title
							startTimeInSeconds
						}
						createdBy {
							thirdPartyUser {
								id
								name
							}
							user {
								name
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();
	const { integrationId } = data;

	// NOTE: The integration ID is the sole indicator currently to differentiate a video entity from Loom vs MS Teams Meeting Transcript.
	// This is because both entities share the same resourceType ('video') and do not necessarily pass a subType or other identifying information.
	// For Meeting Transcripts (Microsoft Teams), we don't show video duration, thumbnail, or chapters - just the transcript excerpt.
	const isMeetingTranscript =
		integrationId === 'ari:cloud:platform::integration/microsoft' &&
		fg('rovo_fps_enable_ms_teams_meeting_type_filter');

	// Past meeting occurrences are hydrated client-side via TWG Cypher query
	// For MS Teams recurring meetings, past occurrences are hydrated client-side via TWG Cypher
	// query (fetchPastMeetingOccurrencesBatch) and passed in as the pastMeetingOccurrences prop.
	// When pastMeetingOccurrences is provided (MS Teams + gate on), it overrides the GraphQL
	// linkedResults from SearchResultGraphDocument (used by Loom and other video types).
	// `category: 'SIMILAR'` is required for correct icon rendering via getLinkedResultIcon.
	// `providerId` is fetched from ExternalVideo.provider.providerId via the Cypher query.
	const linkedResults: LinkedResult[] | undefined =
		pastMeetingOccurrences != null
			? pastMeetingOccurrences.map((occurrence) => ({
					id: occurrence.thirdPartyId ?? occurrence.url,
					title: occurrence.title,
					url: occurrence.url,
					type: entityType,
					description: null,
					lastModifiedDate: occurrence.lastModifiedDate,
					category: 'SIMILAR',
					subtype: null,
					integrationId: data.integrationId,
					providerId: occurrence.providerId,
					connectorType: null,
					durationInSeconds: occurrence.durationInSeconds,
				}))
			: data.linkedResults?.map((linkedResult) => ({
					id: linkedResult.id,
					title: linkedResult.title,
					url: linkedResult.url,
					type: linkedResult.type,
					description: linkedResult.description,
					lastModifiedDate: linkedResult.lastModifiedDate,
					category: linkedResult.category,
					subtype: linkedResult.subtype,
					integrationId: linkedResult.integrationId,
					providerId: linkedResult.providerId,
					connectorType: linkedResult.connectorType,
				}));

	const rendererProps = {
		data,
		formatMessage,
		onResultClicked,
		onResultContextMenu,
		onCopyLinkButtonClicked,
		onSummarizeInChatButtonClicked,
		onContextMenuCopyButtonClicked,
		onContextMenuButtonClicked,
		position,
		cloudId,
		selectedProduct,
		primaryProduct,
		appMode,
		product,
		icon,
		is3pGroupingEnabled,
		thirdPartyConfigs,
		mainContentWidth,
		selectedSites,
		linkedResults,
	};

	if (isMeetingTranscript) {
		return <MeetingTranscriptRenderer {...rendererProps} />;
	}

	return <VideoResultRenderer {...rendererProps} />;
};
